import { API_ROUTES_FOR_AXIOS } from 'src/constants/ApiRoutes';
import { axiosInstance } from 'src/constants/axiosInstance';
import { ApiResponse, PaginatedResponse } from 'src/types/commonResponseTypes';
import {
  ActivityAuditPage,
  ActivityGapAttempts,
  ActivityGapCommand,
  ActivityGapCommandResult,
  ActivityGapIssue,
  ActivityGapPage,
  ActivityGapSort,
  ActivityGapState,
  ActivityPage,
  ActivityScope,
  ActivityStatus,
  InvocationHistoryApiResponse,
  OperationalHealthPage,
  OperationalHealthStatusFilter,
} from 'src/types/invocationHistory';
import { handleError } from 'src/utils/commonErrorUtils';

export const getInvocationHistory = async (
  page: number,
  limit: number,
): Promise<ApiResponse<PaginatedResponse<InvocationHistoryApiResponse[]>>> => {
  try {
    const apiUrl = API_ROUTES_FOR_AXIOS.getInvocationHistory(page, limit);
    const { data } = await axiosInstance.get(apiUrl);

    return data;
  } catch (error) {
    return handleError(error);
  }
};

export const getOperationalFailures = async (input: {
  page: number;
  limit: number;
  query?: string;
  status?: OperationalHealthStatusFilter;
}): Promise<ApiResponse<OperationalHealthPage>> => {
  try {
    const params = new URLSearchParams({
      view: 'system',
      page: String(input.page),
      limit: String(input.limit),
      status: input.status ?? 'all',
    });

    if (input.query) params.set('query', input.query);

    const { data } = await axiosInstance.get(
      API_ROUTES_FOR_AXIOS.getOperationalFailures(params),
    );

    return data;
  } catch (error) {
    return handleError(error);
  }
};

export const decideOperationalFailure = async (
  failureId: string,
  input: {
    decision: 'RETRY' | 'ACKNOWLEDGE';
    failureGeneration: number;
    reason: string;
    idempotencyKey: string;
  },
): Promise<
  ApiResponse<import('src/types/invocationHistory').OperationalHealthItem>
> => {
  try {
    const { data } = await axiosInstance.post(
      API_ROUTES_FOR_AXIOS.decideOperationalFailure(failureId),
      {
        decision: input.decision,
        failureGeneration: input.failureGeneration,
        reason: input.reason,
      },
      {
        headers: {
          'Idempotency-Key': input.idempotencyKey,
          'x-correlation-id': input.idempotencyKey,
        },
      },
    );

    return data;
  } catch (error) {
    return handleError(error);
  }
};

export const getActivity = async (input: {
  page: number;
  limit: number;
  scope: ActivityScope;
  query?: string;
  status?: ActivityStatus;
}): Promise<ApiResponse<ActivityPage>> => {
  try {
    const params = new URLSearchParams({
      page: String(input.page),
      limit: String(input.limit),
      scope: input.scope,
    });

    if (input.query) params.set('query', input.query);
    if (input.status) params.set('status', input.status);

    const { data } = await axiosInstance.get(
      API_ROUTES_FOR_AXIOS.getActivity(params),
    );

    return data;
  } catch (error) {
    return handleError(error);
  }
};

export const getActivityAuditEvents = async (input: {
  page: number;
  limit: number;
  query?: string;
}): Promise<ApiResponse<ActivityAuditPage>> => {
  try {
    const params = new URLSearchParams({
      page: String(input.page),
      limit: String(input.limit),
    });

    if (input.query) params.set('query', input.query);

    const { data } = await axiosInstance.get(
      API_ROUTES_FOR_AXIOS.getActivityAuditEvents(params),
    );

    return data;
  } catch (error) {
    return handleError(error);
  }
};

export const getActivityGaps = async (input: {
  page: number;
  limit: number;
  query?: string;
  lifecycle?: ActivityGapState;
  issue?: ActivityGapIssue;
  sort?: ActivityGapSort;
}): Promise<ApiResponse<ActivityGapPage>> => {
  try {
    const params = new URLSearchParams({
      page: String(input.page),
      limit: String(input.limit),
    });

    if (input.query) params.set('query', input.query);
    if (input.lifecycle) params.set('lifecycle', input.lifecycle);
    if (input.issue) params.set('issue', input.issue);
    if (input.sort) params.set('sort', input.sort);

    const { data } = await axiosInstance.get(
      API_ROUTES_FOR_AXIOS.getActivityGaps(params),
    );

    return data;
  } catch (error) {
    return handleError(error);
  }
};

export const getActivityGapAttempts = async (
  gapId: string,
): Promise<ApiResponse<ActivityGapAttempts>> => {
  try {
    const { data } = await axiosInstance.get(
      API_ROUTES_FOR_AXIOS.getActivityGapAttempts(gapId),
    );

    return data;
  } catch (error) {
    return handleError(error);
  }
};

export const applyActivityGapCommand = async (
  gapId: string,
  input: {
    command: ActivityGapCommand;
    revision: number;
    note?: string;
    idempotencyKey: string;
  },
): Promise<ApiResponse<ActivityGapCommandResult>> => {
  try {
    const { data } = await axiosInstance.post(
      API_ROUTES_FOR_AXIOS.applyActivityGapCommand(gapId),
      {
        command: input.command,
        revision: input.revision,
        note: input.note,
      },
      {
        headers: {
          'Idempotency-Key': input.idempotencyKey,
          'x-correlation-id': input.idempotencyKey,
        },
      },
    );

    return data;
  } catch (error) {
    return handleError(error);
  }
};

export const getInvocationHistoryForSuperAdmin = async (
  page: number,
  limit: number,
  orgId?: string,
  userId?: string,
): Promise<ApiResponse<PaginatedResponse<InvocationHistoryApiResponse[]>>> => {
  try {
    const apiUrl = API_ROUTES_FOR_AXIOS.getInvocationHistoryForSuperAdmin(
      page,
      limit,
      orgId,
      userId,
    );
    const { data } = await axiosInstance.get(apiUrl);

    return data;
  } catch (error) {
    return handleError(error);
  }
};

export const searchInvocationHistory = async (
  page: number,
  limit: number,
  search: string,
): Promise<ApiResponse<PaginatedResponse<InvocationHistoryApiResponse[]>>> => {
  try {
    const apiUrl = API_ROUTES_FOR_AXIOS.searchInvocationHistory(
      page,
      limit,
      search,
    );
    const { data } = await axiosInstance.get(apiUrl);

    return data;
  } catch (error) {
    return handleError(error);
  }
};

export const searchInvocationHistoryForSuperAdmin = async (
  page: number,
  limit: number,
  search: string,
  orgId?: string,
  userId?: string,
): Promise<ApiResponse<PaginatedResponse<InvocationHistoryApiResponse[]>>> => {
  try {
    const apiUrl = API_ROUTES_FOR_AXIOS.searchInvocationHistoryForSuperAdmin(
      page,
      limit,
      search,
      orgId,
      userId,
    );
    const { data } = await axiosInstance.get(apiUrl);

    return data;
  } catch (error) {
    return handleError(error);
  }
};

export const updateInvocationHistoryState = async (
  allowInvocationHistory: boolean,
): Promise<ApiResponse<InvocationHistoryApiResponse>> => {
  try {
    const apiUrl = API_ROUTES_FOR_AXIOS.updateInvocationHistoryState();
    const { data } = await axiosInstance.patch(apiUrl, {
      allowInvocationHistory,
    });

    return data;
  } catch (error) {
    return handleError(error);
  }
};

export const updateInvocationHistoryStateForOrg = async (
  orgId: string,
  allowInvocationHistory: boolean,
): Promise<ApiResponse<InvocationHistoryApiResponse>> => {
  try {
    const apiUrl =
      API_ROUTES_FOR_AXIOS.updateInvocationHistoryStateForOrg(orgId);
    const { data } = await axiosInstance.patch(apiUrl, {
      allowInvocationHistory,
    });

    return data;
  } catch (error) {
    return handleError(error);
  }
};
